import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Divider, Modal, Space } from 'antd';

import moment from 'moment';
import { PageProps } from '@/types/PageProps';
import {
  AppPageHeader,
  ColumnsType,
  InfiniteTable,
  TabContentWrapper,
} from '@/components/_common';

import CreateInventoryModal from '@/components/Movements/Inventory/Create/CreateInventoryModal';
import {
  useInventories,
  useRemoveInventory,
} from '@/core/domain/inventory/inventories/inventory.hooks';
import { flatInfiniteData } from '../../../utils/request/flatInfiniteData';
import getInventoryStatus from '@/core/domain/inventory/inventories/getInventoriesStatus';
import getInventoryReasons from '@/core/domain/inventory/inventories/getInventoriesReasons';
import { getTableScrollX } from '@/utils/table';
import { useDebounce, useDisclosure } from '@/hooks';
import {
  Inventory,
  InventoryFilterProps,
  InventoryStatus,
  InventoryStatusColor,
} from '@/core/domain/inventory/inventories/Inventory.types';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import InventoryFilters from './InventoryFilters';
import { useState } from 'react';
import Authorities from '@/types/Authority';
import StatusTag from '@/components/_common/StatusTag';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { hasPermissions } from '@/utils/functions/hasPermissions';

function InventoriesPage({ route }: PageProps) {
  const intl = useIntl();

  const [createModal, createModalAction] = useDisclosure();
  const [queryParams, setQueryParams] = useState<InventoryFilterProps>({
    order: 'DESC',
    ...convertParams<InventoryFilterProps>({
      companyId: 'number',
      date: 'string',
      reason: 'string',
      status: 'string',
      search: 'string',
      order: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { data, isFetching, hasNextPage, fetchNextPage } =
    useInventories(queryDebounce);

  const inventory = flatInfiniteData(data);

  const removeMutation = useRemoveInventory();

  const handleRemoveClick = (inventory: Inventory) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'inventory.notifications.remove.confirm.title',
      }),
      content: `${intl.formatMessage({
        id: 'inventory.notifications.remove.confirm.content',
      })}`,
      onOk: async () => {
        await removeMutation.mutateAsync(inventory.id);
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<Inventory> = [
    {
      title: intl.formatMessage({
        id: 'inventory.columns.table.company',
      }),
      dataIndex: ['company', 'name'],
      key: 'companyName',
      ellipsis: true,
      width: 300,
    },
    {
      title: intl.formatMessage({
        id: `inventory.columns.table.date`,
      }),
      dataIndex: 'date',
      key: 'date',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      ellipsis: true,
      width: 150,
    },
    {
      title: intl.formatMessage({
        id: `inventory.columns.table.inventoryReason`,
      }),
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      width: 360,
      render: (value) => {
        const reasonFinded = getInventoryReasons().find(
          (reason) => reason.value === value,
        );
        return reasonFinded
          ? intl.formatMessage({ id: reasonFinded.labelId })
          : '-';
      },
    },
    {
      title: intl.formatMessage({
        id: `inventory.columns.table.situation`,
      }),
      dataIndex: ['currentCount', 'countNumber'],
      key: 'status',
      ellipsis: true,
      width: 180,
      render: (value: InventoryStatus, row) => {
        if (
          row.status === InventoryStatus.PROCESSING ||
          row.status === InventoryStatus.IMPORTING ||
          row.status === InventoryStatus.DONE
        ) {
          const reasonFinded = getInventoryStatus().find(
            (status) => status.value === row.status,
          );
          return reasonFinded ? (
            <StatusTag
              label={intl.formatMessage({ id: reasonFinded.labelId })}
              color={InventoryStatusColor[row.status]}
            />
          ) : (
            '-'
          );
        } else {
          const count = value;
          return (
            <>
              <StatusTag
                label={
                  count +
                  intl.formatMessage({
                    id: 'inventory.detailPage.fetchItems.message.firstCount',
                  })
                }
                color="blue"
              />
              {row.status === InventoryStatus.ERROR && (
                <>
                  <Divider type="vertical" />
                  <StatusTag label="Erro" color="red" />
                </>
              )}
            </>
          );
        }
      },
    },
    {
      key: 'actions',
      title: intl.formatMessage({
        id: 'commons.list.header.action',
      }),
      align: 'right',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Space>
          <EditIcon
            redirect={`/app/movements/inventory/${record.id}`}
            disabled={
              !hasPermissions([Authorities.MOVIMENTATION_INVENTORY_EDIT])
            }
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(record)}
            disabled={
              !hasPermissions([Authorities.MOVIMENTATION_INVENTORY_DELETE]) ||
              record.status === InventoryStatus.DONE ||
              record.status === InventoryStatus.PROCESSING
            }
          />
        </Space>
      ),
      ellipsis: true,
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <TabContentWrapper
        title={route.name}
        permissions={route.permissions}
        extra={
          <Button
            type="primary"
            onClick={() => createModalAction.toggle()}
            block
            disabled={
              !hasPermissions([Authorities.MOVIMENTATION_INVENTORY_ADD])
            }
          >
            <FormattedMessage id="inventory.create" />
          </Button>
        }
      >
        <AppPageHeader
          onSearch={(value) =>
            setQueryParams({ ...queryParams, companyName: value })
          }
          searchValue={queryParams.companyName}
          headerActions={({ isShowingFilters }) =>
            isShowingFilters && (
              <Button
                type="primary"
                onClick={() => setQueryParams({ order: 'DESC' })}
              >
                {intl.formatMessage({ id: 'commons.clear.filters' })}
              </Button>
            )
          }
          extraFilters={
            <InventoryFilters
              setQueryParams={setQueryParams}
              queryParams={queryParams}
            />
          }
        />
        <InfiniteTable<Inventory>
          size="small"
          loading={isFetching}
          rowKey={(row) => row.id}
          dataSource={inventory}
          columns={columns}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          scroll={{ x: getTableScrollX(columns) }}
        />
      </TabContentWrapper>

      <CreateInventoryModal
        visible={createModal}
        handleVisible={() => createModalAction.toggle()}
      />
    </>
  );
}

export default InventoriesPage;
